import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  FullScreenMode,
  InputSource,
  InterviewConfig,
  InterviewMode,
  InterviewType,
  LabEnvironment,
  PollSessionResponse,
  Problem,
  Violations,
  WaitingLobbyStageType,
} from "types/interview"
import { SessionStatus, SessionType } from "utils/interview"

interface TestSummary {
  [key: string]: {
    description?: string | null
    blob?: Blob | null
  }
}

interface InterviewState {
  config: InterviewConfig | null
  interviewStage: string | null
  testSessionStatus: SessionStatus | null
  startTime: number
  endTime: number
  duration: number
  expiryStartTime: number
  testSummary: TestSummary
  showProblemModal: boolean
  waitingLobbyStage: WaitingLobbyStageType
  testDetailsForm: Record<string, string> | null
  testDetailsFormUrls: Record<string, string> | {}
  sessionId: string | null
  sessionType: SessionType | null
  problem: Problem | null
  labEnvironment: LabEnvironment | null
  labEnvironmentServerData: PollSessionResponse["data"] | null
  interviewType: InterviewType | null
  fullScreenMode: FullScreenMode
  inputSource: InputSource
  isCameraOn: boolean
  restrictionsEnabled: boolean
  violations: Violations
  interviewMode: InterviewMode
  interviewSlug: string | null
  isDisqualified: boolean
  maxAllowedViolations: number
  animatedWordIndex: number
  isRecording: boolean
  isBotSpeaking: boolean
  fileName: File | null
  dualCameraRequired?: boolean
  dualCameraPaired?: boolean
  mWebModalStage: string
  mWebModalIsOpen: boolean
  interviewTestConfig: any
}

const initialState: InterviewState = {
  config: null,
  interviewStage: null,
  testSessionStatus: null,
  testSummary: {},
  showProblemModal: false,
  startTime: 0,
  endTime: 0,
  duration: 0,
  expiryStartTime: 0,
  waitingLobbyStage: 0,
  testDetailsForm: null,
  testDetailsFormUrls: {},
  sessionId: null,
  sessionType: null,
  interviewType: null,
  fileName: null,
  fullScreenMode: null,
  problem: null,
  labEnvironment: null,
  labEnvironmentServerData: null,
  restrictionsEnabled: false,
  isCameraOn: true,
  inputSource: {
    audioInput: null,
    videoInput: null,
  },
  animatedWordIndex: 0,
  interviewMode: "non_proctored",
  interviewSlug: null,
  isDisqualified: false,
  isRecording: false,
  maxAllowedViolations: 5,
  isBotSpeaking: false,
  violations: {
    fullScreenExit: 0,
    tabSwitch: 0,
    copyPaste: 0,
    pageReload: 0,
    screenShareExit: 0,
    mobileCameraDisconnected: 0,
  },
  dualCameraRequired: false,
  dualCameraPaired: false,
  mWebModalStage: "settingStage",
  mWebModalIsOpen: false,
  interviewTestConfig: null,
}

const InterviewSlice = createSlice({
  name: "interview",
  initialState,
  reducers: {
    setConfig: (state, action: PayloadAction<InterviewConfig>) => {
      state.config = action.payload
    },
    setInterviewStage: (state, action: PayloadAction<string>) => {
      state.interviewStage = action.payload
    },
    setTestSessionStatus: (state, action: PayloadAction<SessionStatus>) => {
      state.testSessionStatus = action.payload
    },
    setTestSummary: (state, action: PayloadAction<TestSummary>) => {
      state.testSummary = action.payload
    },
    setShowProblemModal: (state, action: PayloadAction<boolean>) => {
      state.showProblemModal = action.payload
    },
    setStartTime: (state, action: PayloadAction<number>) => {
      state.startTime = action.payload
    },
    setEndTime: (state, action: PayloadAction<number>) => {
      state.endTime = action.payload
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload
    },
    setExpiryStartTime: (state, action: PayloadAction<number>) => {
      state.expiryStartTime = action.payload
    },
    setWaitingLobbyStage: (state, action: PayloadAction<WaitingLobbyStageType>) => {
      state.waitingLobbyStage = action.payload
    },
    setTestDetailsForm: (state, action: PayloadAction<Record<string, string>>) => {
      state.testDetailsForm = action.payload
    },
    setTestDetailsFormUrls: (state, action: PayloadAction<Record<string, string>>) => ({
      ...state,
      testDetailsFormUrls: {
        ...state.testDetailsFormUrls,
        ...action.payload,
      },
    }),
    setSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload
    },
    setSessionType: (state, action: PayloadAction<SessionType | null>) => {
      state.sessionType = action.payload
    },
    setProblem: (state, action: PayloadAction<Problem | null>) => {
      state.problem = action.payload
    },
    setLabEnvironment: (state, action: PayloadAction<LabEnvironment | null>) => {
      state.labEnvironment = action.payload
    },
    setLabEnvironmentServerData: (state, action: PayloadAction<PollSessionResponse["data"] | null>) => {
      state.labEnvironmentServerData = action.payload
    },
    setInterviewType: (state, action: PayloadAction<InterviewType | null>) => {
      state.interviewType = action.payload
    },
    setFileName: (state, action: PayloadAction<File | null>) => {
      state.fileName = action.payload
    },
    setInputSource: (state, action: PayloadAction<InputSource>) => {
      state.inputSource = action.payload
    },
    setIsCameraOn: (state, action: PayloadAction<boolean>) => {
      state.isCameraOn = action.payload
    },
    setFullScreenMode: (state, action: PayloadAction<FullScreenMode>) => {
      state.fullScreenMode = action.payload
    },
    setInterviewSlug: (state, action: PayloadAction<string>) => {
      state.interviewSlug = action.payload
    },
    setInterviewMode: (state, action: PayloadAction<InterviewMode>) => {
      state.interviewMode = action.payload
    },
    setRestrictionsEnabled: (state, action: PayloadAction<boolean>) => {
      state.restrictionsEnabled = action.payload
    },
    recordViolation: (state, action: PayloadAction<keyof Violations>) => {
      state.violations[action.payload] += 1
    },
    resetViolations: (state) => {
      state.violations = initialState.violations
    },
    setIsDisqualified: (state, action: PayloadAction<boolean>) => {
      state.isDisqualified = action.payload
    },
    setIsRecording: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload
    },
    setAnimatedWordIndex: (state, action: PayloadAction<number>) => {
      state.animatedWordIndex = action.payload
    },
    setIsBotSpeaking: (state, action: PayloadAction<boolean>) => {
      state.isBotSpeaking = action.payload
    },
    setMaxAllowedViolations: (state, action: PayloadAction<number>) => {
      state.maxAllowedViolations = action.payload
    },
    setDualCameraRequired: (state, action: PayloadAction<boolean>) => {
      state.dualCameraRequired = action.payload
    },
    setDualCameraPaired: (state, action: PayloadAction<boolean>) => {
      state.dualCameraPaired = action.payload
    },
    setMWebModalStage: (state, action: PayloadAction<string>) => {
      state.mWebModalStage = action.payload
    },
    setMWebModalIsOpen: (state, action: PayloadAction<boolean>) => {
      state.mWebModalIsOpen = action.payload
    },
    setInterviewTestConfig: (state, action: PayloadAction<any>) => {
      state.interviewTestConfig = action.payload
    },
    resetInterview: () => initialState,
  },
})

export const {
  setConfig,
  setInterviewStage,
  setTestSessionStatus,
  setTestSummary,
  setShowProblemModal,
  setStartTime,
  setEndTime,
  setDuration,
  setExpiryStartTime,
  setWaitingLobbyStage,
  setTestDetailsForm,
  setTestDetailsFormUrls,
  setSessionId,
  setSessionType,
  setProblem,
  setLabEnvironment,
  setLabEnvironmentServerData,
  setInterviewType,
  setFileName,
  setInputSource,
  setIsCameraOn,
  setFullScreenMode,
  setRestrictionsEnabled,
  recordViolation,
  resetViolations,
  setInterviewMode,
  setInterviewSlug,
  setIsDisqualified,
  resetInterview,
  setIsRecording,
  setIsBotSpeaking,
  setAnimatedWordIndex,
  setMaxAllowedViolations,
  setDualCameraRequired,
  setDualCameraPaired,
  setMWebModalStage,
  setMWebModalIsOpen,
  setInterviewTestConfig,
} = InterviewSlice.actions
export default InterviewSlice.reducer
